import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CART_RESOURCE_URL = `${window.Shopify?.routes?.root || '/'}cart.js`;
const CLUB_REWARD_PREFERENCE_KEY = 'simpocubic-club-reward-preference';

const getClubRewardPreference = () => {
  try {
    const preference = window.sessionStorage.getItem(CLUB_REWARD_PREFERENCE_KEY);
    return preference === 'kit' || preference === 'free' ? preference : '';
  } catch (_) {
    return '';
  }
};

const setClubRewardPreference = (preference) => {
  try {
    window.sessionStorage.setItem(CLUB_REWARD_PREFERENCE_KEY, preference);
  } catch (_) {
    // Default selections still work when session storage is unavailable.
  }
};

class ClubProductRewardSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);

    const storedPreference = getClubRewardPreference();
    const preferredInput = this.querySelector(`input[type="radio"][value="${storedPreference}"]:not(:disabled)`);
    const defaultInput = this.querySelector(`input[type="radio"][value="${this.dataset.default}"]:not(:disabled)`);
    const firstAvailableInput = this.querySelector('input[type="radio"]:not(:disabled)');
    const selectedInput = preferredInput || defaultInput || firstAvailableInput;

    if (selectedInput instanceof HTMLInputElement) {
      selectedInput.checked = true;
      this.#apply(selectedInput.value, false);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.checked) return;
    this.#apply(target.value, true);
  };

  #apply(rewardType, remember) {
    const additionalItemInput = this.querySelector('[data-club-additional-item-id]');
    const status = this.querySelector('.simpocubic-club-product-reward__status');
    const variantId = rewardType === 'kit' ? this.dataset.kitVariant : this.dataset.freeVariant;
    const confirmation = rewardType === 'kit' ? this.dataset.kitConfirmation : this.dataset.freeConfirmation;

    if (additionalItemInput instanceof HTMLInputElement && variantId) additionalItemInput.value = variantId;
    if (status) status.textContent = confirmation || '';
    if (remember) setClubRewardPreference(rewardType);
  }
}

if (!customElements.get('club-product-reward-selector')) {
  customElements.define('club-product-reward-selector', ClubProductRewardSelector);
}

class ClubRewardSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    if (this.dataset.cleanup === 'true') this.#sync('');
    else if (this.dataset.autoAdd === 'true' && !this.dataset.current) this.#sync(getClubRewardPreference() || 'free');
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === 'simpocubic-club-reward') return;

    const items = event.detail?.resource?.items;
    if (!Array.isArray(items)) return;

    const hasManagedReward = items.some((item) => item.properties?._club_reward);
    const hasKeycapSet = items.some((item) => item.product_type === 'Keycap Set');

    if (hasManagedReward && !hasKeycapSet) this.#sync('');
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    setClubRewardPreference(target.value);
    this.#sync(target.value);
  };

  async #sync(rewardType) {
    if (window.__simpocubicClubRewardBusy) return;
    if (rewardType && this.dataset.current === rewardType) return;

    window.__simpocubicClubRewardBusy = true;
    this.setAttribute('aria-busy', 'true');
    this.#setControlsDisabled(true);
    this.#setStatus('', false);

    try {
      const cart = await this.#fetchCart();
      const managedItems = cart.items.filter((item) => item.properties?._club_reward);
      const sections = this.#sectionIds();

      if (managedItems.length) {
        const updates = Object.fromEntries(managedItems.map((item) => [item.key, 0]));
        await this.#post(Theme.routes.cart_update_url, {
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });
      }

      if (rewardType) {
        const variantId = rewardType === 'kit' ? this.dataset.kitVariant : this.dataset.quadpullVariant;
        const visibleLabel = rewardType === 'kit' ? this.dataset.kitLabel : this.dataset.freeLabel;
        await this.#post(Theme.routes.cart_add_url, {
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: {
              _club_reward: rewardType,
              'Club reward': visibleLabel,
            },
          }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });
      }

      const nextCart = await this.#fetchCart();
      this.dataset.current = rewardType;
      this.dispatchEvent(new CartUpdateEvent(nextCart, this.id || 'simpocubic-club-reward', {
        source: 'simpocubic-club-reward',
        itemCount: nextCart.item_count,
      }));
    } catch (error) {
      console.error('SimpoCubic Club reward update failed', error);
      this.#setStatus(this.dataset.error || 'Unable to update the Club reward. Please try again.', true);
    } finally {
      window.__simpocubicClubRewardBusy = false;
      this.removeAttribute('aria-busy');
      this.#setControlsDisabled(false);
    }
  }

  async #fetchCart() {
    const response = await fetch(CART_RESOURCE_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  async #post(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    return data;
  }

  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);
  }

  #setControlsDisabled(disabled) {
    this.querySelectorAll('input[type="radio"]').forEach((input) => { input.disabled = disabled; });
  }

  #setStatus(message, isError) {
    const status = this.querySelector('.simpocubic-club-reward__status');
    if (!status) return;
    status.textContent = message;
    status.dataset.error = isError ? 'true' : 'false';
  }
}

if (!customElements.get('club-reward-selector')) {
  customElements.define('club-reward-selector', ClubRewardSelector);
}

const syncClubAccountPopover = (account) => {
  const root = account.shadowRoot;
  if (!root) return;

  const desiredHeading = account.dataset.clubAccountHeading;
  const heading = root.querySelector('.account__heading');
  if (heading && desiredHeading && heading.textContent !== desiredHeading) {
    heading.textContent = desiredHeading;
  }

  const menu = root.querySelector('.account__shortcuts');
  if (menu && account.dataset.clubAccountLink && account.dataset.clubAccountUrl) {
    let item = menu.querySelector('[data-club-account-item]');
    if (!item) {
      item = document.createElement('div');
      item.className = 'account__item-wrapper';
      item.dataset.clubAccountItem = '';
      item.innerHTML = '<a class="account__item" target="_top"><span class="account__item-text"></span></a>';
      menu.prepend(item);
    }

    const link = item.querySelector('a');
    const linkText = link?.querySelector('.account__item-text');

    if (link && link.href !== account.dataset.clubAccountUrl) {
      link.href = account.dataset.clubAccountUrl;
    }

    if (linkText && linkText.textContent !== account.dataset.clubAccountLink) {
      linkText.textContent = account.dataset.clubAccountLink;
    }
  }
};

customElements.whenDefined('shopify-account').then(() => {
  document.querySelectorAll('shopify-account[data-club-account-heading]').forEach((account) => {
    syncClubAccountPopover(account);
    if (!account.shadowRoot) return;
    const observer = new MutationObserver(() => syncClubAccountPopover(account));
    observer.observe(account.shadowRoot, { childList: true, subtree: true });
  });
});

const clubJoinForm = document.querySelector('[data-club-join-form]');
const clubJoinUrl = new URL(window.location.href);
const CLUB_JOIN_INTENT_KEY = 'simpocubic-club-join';

document.querySelectorAll('[data-club-login-link]').forEach((link) => {
  link.addEventListener('click', () => {
    try {
      window.sessionStorage.setItem(CLUB_JOIN_INTENT_KEY, '1');
    } catch (_) {
      // The logged-in Club page still provides a manual join button when storage is unavailable.
    }
  });
});

let hasClubJoinIntent = clubJoinUrl.searchParams.get('join_club') === '1';
try {
  hasClubJoinIntent ||= window.sessionStorage.getItem(CLUB_JOIN_INTENT_KEY) === '1';
} catch (_) {
  // Fall back to the legacy query flag when storage is unavailable.
}

if (clubJoinForm instanceof HTMLFormElement && hasClubJoinIntent) {
  clubJoinUrl.searchParams.delete('join_club');
  window.history.replaceState({}, '', `${clubJoinUrl.pathname}${clubJoinUrl.search}${clubJoinUrl.hash}`);
  try {
    window.sessionStorage.removeItem(CLUB_JOIN_INTENT_KEY);
  } catch (_) {
    // The intent is already consumed for this page load.
  }
  window.requestAnimationFrame(() => clubJoinForm.requestSubmit());
}
